/**
 * The save control on a 词典 entry, for a reader who has a session.
 *
 * That page is served from the edge, one set of bytes to everybody, so the
 * button arrives saying `Save` whether or not the reader already holds the
 * word. Two things happen here and neither is needed for the control to work.
 * The buttons for words this reader holds are relabelled, and a press posts
 * where it stands instead of landing the reader on their list.
 *
 * **Nothing here is load-bearing.** With the script blocked, the form posts,
 * `CiPage` turns the word over and answers with `/user/ci/`, which is the list
 * with the word in it. That is the arrangement every account control on this
 * site keeps, and `src/site/download-offer.ts` states it for the vocabulary
 * downloads. What the script buys is the reader staying on the page they were
 * reading.
 *
 * **A signed-out reader is offered an account in place.** The offer is in the
 * markup and `hidden`, and a press reveals it rather than taking the reader to
 * Cognito. The sign-in link inside it is the one `src/site/sign-in-link.ts`
 * puts the current page on.
 *
 * **It is revealed on a press rather than on sight**, which is where this parts
 * company with the vocabulary downloads. #499 turns those two links off for a
 * reader the page can see is signed out, on the grounds that the file is what
 * they came for and a dead link with a reason beats a download that fails. A
 * save control is not what anybody came to a 词典 entry for, and an account
 * offer drawn under every one of 22,332 entries would be furniture on every
 * page of the section.
 *
 * The other two thirds of this are `save-word-control.ts`, which is the markup,
 * and `save-word-route.ts`, which is what the handler is asked.
 */

import {
  buttonOf,
  drawnAsHeld,
  drawSavedWord,
  postWithoutScript,
  revealAccountOffer,
} from "./save-word-control.js";
import {
  askHeldWords,
  refusedLocation,
  toggleSavedWord,
} from "./save-word-route.js";

/** The block holding one word's form and the offer under it. */
const controlSelector = ".cb-save-word";

/** The form inside it. */
const formSelector = ".cb-save-word-form";

/**
 * What `head/session-state.html` writes on the document for a reader carrying a
 * session.
 *
 * The test is against this value rather than against its absence, in the shape
 * `download-offer.ts` takes. A page whose head script never ran carries no
 * attribute, and the fallback for a signed-in reader is the form posting the
 * way it does with no script at all.
 */
const signedIn = "in";

/** Whether the page says there is a reader to ask the route about. */
function hasSession(document: Document): boolean {
  return document.documentElement.dataset["cbSession"] === signedIn;
}

/**
 * Say which of the words on this page the reader already holds.
 *
 * Asked only about the buttons the page could not say for itself. A control
 * drawn `held` is one `/user/ci/` wrote out of the reader's own session, so the
 * route would only hand back the word it was given.
 */
async function markHeld(
  buttons: readonly HTMLButtonElement[],
  fetched: typeof fetch,
): Promise<void> {
  const held =
    (await askHeldWords(
      fetched,
      buttons.map((button) => button.value),
    )) ?? [];

  for (const button of buttons.filter((one) => held.includes(one.value))) {
    drawSavedWord(button, true);
  }
}

/**
 * Turn one word over without leaving the page.
 *
 * A reader at the cap is sent to their list, where the offer of Pro 专业 and the
 * words they would have to drop are both drawn. That page is the one place the
 * cap is explained, so the script goes there rather than carrying a second copy
 * of the words.
 *
 * A session that has lapsed since the page was drawn gets the account offer, the
 * same as anybody signed out.
 */
async function press(properties: {
  button: HTMLButtonElement;
  control: HTMLElement;
  fetched: typeof fetch;
}): Promise<void> {
  const word = properties.button.value;
  const result = await toggleSavedWord(properties.fetched, word);

  if (result.full) {
    globalThis.location.assign(refusedLocation(word));
  } else if (result.signedOut) {
    revealAccountOffer(properties.control);
  } else {
    drawSavedWord(properties.button, result.held);
  }
}

/** Take the presses on one control. */
function listen(properties: {
  control: HTMLElement;
  document: Document;
  fetched: typeof fetch;
}): void {
  const { control, document, fetched } = properties;
  const form = control.querySelector<HTMLFormElement>(formSelector);
  const button = buttonOf(control);

  if (form === null || button === null) {
    return;
  }

  form.addEventListener("submit", (event: Event) => {
    event.preventDefault();

    if (!hasSession(document)) {
      revealAccountOffer(control);

      return;
    }

    void press({ button, control, fetched }).catch(() => {
      postWithoutScript(form, button.value);
    });
  });
}

/**
 * Takes the presses on every save control on the page.
 *
 * Returns on a page with no control, which is every page of this site but the
 * 词典 entries and `/user/ci/`.
 * @param document - The page the bundle is running on.
 * @param fetched - What asks the route, which a test hands its own.
 */
export function startSaveWords(
  document: Document,
  fetched: typeof fetch = globalThis.fetch,
): void {
  const controls = [...document.querySelectorAll<HTMLElement>(controlSelector)];
  const buttons = controls
    .map((control) => buttonOf(control))
    .filter((button) => button !== null);

  if (buttons.length === 0) {
    return;
  }

  const asking = buttons.filter((button) => !drawnAsHeld(button));

  if (hasSession(document) && asking.length > 0) {
    void markHeld(asking, fetched).catch(() => undefined);
  }

  for (const control of controls) {
    listen({ control, document, fetched });
  }
}
